import { memo, type SyntheticEvent, useEffect, useState } from 'react';
import { Select, type SelectProps } from 'antd';
import debounce from 'lodash/debounce';

type OmitKeys = 'loading' | 'fieldNames' | 'optionFilterProp';
type OptionType = Record<string, any> & { id: string };

interface Props extends Omit<SelectProps<string, OptionType>, OmitKeys> {
  options: Array<OptionType>;
  labelKey: string;
  loading?: boolean;
  hasMoreResults: boolean;
  onLoadNextPage: () => void;
  onSearch?: (value: string) => void;
}

const { Option } = Select;

const InfiniteLoadingSelect = memo(
  ({
    loading,
    options,
    labelKey,
    hasMoreResults,
    onLoadNextPage,
    onSearch,
    ...props
  }: Props) => {
    const [currentOptions, setCurrentOptions] = useState<
      Map<string, OptionType>
    >(new Map());

    const handleLoadMore = (e: SyntheticEvent) => {
      const target = e.target as HTMLElement;
      const reachedBottom =
        target.scrollTop + target.offsetHeight === target.scrollHeight;

      if (reachedBottom && !loading) {
        hasMoreResults && onLoadNextPage();
      }
    };
    const handleOnSearch = debounce((value: string) => {
      onSearch?.(value);
    }, 500);

    useEffect(() => {
      if (options?.length) {
        setCurrentOptions(
          (prev) =>
            new Map(
              [...prev.values(), ...options].map((option) => [
                option.id,
                option,
              ])
            )
        );
      }
    }, [options]);

    return (
      <Select
        {...props}
        loading={loading}
        onPopupScroll={handleLoadMore}
        onSearch={handleOnSearch}
      >
        {[...currentOptions.values()].map((option) => (
          <Option key={option.id} value={option.id}>
            {option[labelKey]}
          </Option>
        ))}

        {loading && <Option>Loading...</Option>}
      </Select>
    );
  }
);

export default InfiniteLoadingSelect;
